import { useState, useEffect } from 'react';
import { RoadmapItem, Project, LinearResponse, ProjectsResponse } from '../types';

export const useLinearData = () => {
    const [issues, setIssues] = useState<RoadmapItem[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchLinearData = async () => {
            try {
                setLoading(true);
                const envApiUrl = process.env.REACT_APP_API_URL?.trim();
                const isProd = process.env.NODE_ENV === 'production';
                const apiUrl = envApiUrl || (isProd ? '' : 'http://localhost:3000');

                if (!apiUrl) {
                    throw new Error('REACT_APP_API_URL is missing for production build.');
                }

                const [issuesResponse, projectsResponse] = await Promise.all([
                    fetch(`${apiUrl}/api/linear`),
                    fetch(`${apiUrl}/api/projects`)
                ]);

                if (!issuesResponse.ok || !projectsResponse.ok) {
                    const issuesErrorBody = await issuesResponse.text();
                    const projectsErrorBody = await projectsResponse.text();

                    let issuesErrorMessage = `HTTP ${issuesResponse.status}`;
                    let projectsErrorMessage = `HTTP ${projectsResponse.status}`;

                    try {
                        const parsedIssuesError = JSON.parse(issuesErrorBody);
                        if (parsedIssuesError?.error) {
                            issuesErrorMessage = parsedIssuesError.error;
                        }
                    } catch (_error) {
                        // Non-JSON response body; keep generic HTTP status message.
                    }

                    try {
                        const parsedProjectsError = JSON.parse(projectsErrorBody);
                        if (parsedProjectsError?.error) {
                            projectsErrorMessage = parsedProjectsError.error;
                        }
                    } catch (_error) {
                        // Non-JSON response body; keep generic HTTP status message.
                    }

                    throw new Error(`API error: ${issuesErrorMessage} / ${projectsErrorMessage}`);
                }

                const issuesData: LinearResponse = await issuesResponse.json();
                const projectsData: ProjectsResponse = await projectsResponse.json();

                setIssues(issuesData.data.issues.nodes);
                setProjects(projectsData.data.projects.nodes);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch data');
                console.error('Error fetching Linear data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchLinearData();
    }, []);

    return { issues, projects, loading, error };
};

const getApiBaseUrl = (): string => {
    const envApiUrl = process.env.REACT_APP_API_URL?.trim();
    const isProd = process.env.NODE_ENV === 'production';
    return envApiUrl || (isProd ? '' : 'http://localhost:3000');
};

export interface RobloxPlayerCountResponse {
    placeId: number;
    universeId: number;
    playing: number;
    cached?: boolean;
}

export const useRobloxPlayerCount = (placeId = 12599929830) => {
    const [playing, setPlaying] = useState<number | null>(null);

    useEffect(() => {
        const fetchPlayerCount = async () => {
            try {
                const apiUrl = getApiBaseUrl();
                if (!apiUrl) return;

                const response = await fetch(`${apiUrl}/api/roblox/player-count?placeId=${placeId}`);
                if (!response.ok) return;

                const data: RobloxPlayerCountResponse = await response.json();
                if (typeof data.playing === 'number') {
                    setPlaying(data.playing);
                }
            } catch {
                // Keep play button usable even if live count is unavailable.
            }
        };

        fetchPlayerCount();
    }, [placeId]);

    return playing;
};

export const useScrollBanner = () => {
    const [showBanner, setShowBanner] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const scrollPercent = docHeight > 0 ? (scrollY / docHeight) : 0;
            setShowBanner(scrollPercent > 0.95);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return showBanner;
};
